import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  Container,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../../components/common/confirm-dialog";
import TenantUpsertDialog, { TenantForEdit } from "../../components/admin/tenant-upsert-dialog";
import SearchBar from "../../components/common/search-bar";
import GenericTable, { ColumnDefinition } from "../../components/common/generic-table";
import {
  FeatureDto,
  TenantDto,
  UpsertTenantRequest,
  useTenantManagementService,
} from "../../services/tenantManagementService";
import { useSnackbar } from "../../contexts/snackbar-context";

const toUiStatusLabel = (apiStatus: string) =>
  apiStatus === "Suspended" ? "Inactive" : "Active";

const TenantManagement: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const {
    getTenants,
    getAvailableFeatures,
    getTenantFeatureKeys,
    addTenant,
    updateTenant,
    deleteTenant,
  } = useTenantManagementService();

  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<FeatureDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [tenantFeaturesLoading, setTenantFeaturesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit">("add");
  const [selectedTenant, setSelectedTenant] = useState<TenantForEdit | null>(null);
  const [selectedTenantFeatureKeys, setSelectedTenantFeatureKeys] = useState<string[]>([]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantPendingDelete, setTenantPendingDelete] = useState<TenantDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const tenantById = useMemo(() => {
    return new Map(tenants.map((t) => [t.id, t] as const));
  }, [tenants]);

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const list = await getTenants();
      setTenants(list);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load tenants";
      setError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [getTenants, showError]);

  const loadFeaturesIfNeeded = useCallback(async () => {
    if (availableFeatures.length > 0) return;

    try {
      setFeaturesLoading(true);
      const list = await getAvailableFeatures();
      setAvailableFeatures(list);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load features";
      showError(message);
      setAvailableFeatures([]);
    } finally {
      setFeaturesLoading(false);
    }
  }, [availableFeatures.length, getAvailableFeatures, showError]);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  const openAdd = async () => {
    setDialogMode("add");
    setSelectedTenant(null);
    setSelectedTenantFeatureKeys([]);
    setDialogOpen(true);
    await loadFeaturesIfNeeded();
  };

  const openEdit = async (tenantId: string) => {
    const tenant = tenantById.get(tenantId);
    if (!tenant) return;

    setDialogMode("edit");
    setSelectedTenant({ id: tenant.id, name: tenant.name, status: tenant.status , logoUrl: tenant.logoUrl});
    setSelectedTenantFeatureKeys([]);
    setDialogOpen(true);

    await loadFeaturesIfNeeded();

    try {
      setTenantFeaturesLoading(true);
      const keys = await getTenantFeatureKeys(tenantId);
      setSelectedTenantFeatureKeys(keys);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to load tenant features";
      showError(message);
      setSelectedTenantFeatureKeys([]);
    } finally {
      setTenantFeaturesLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedTenant(null);
    setSelectedTenantFeatureKeys([]);
  };

  const handleSave = async (request: UpsertTenantRequest) => {
    if (dialogMode === "add") {
      const ok = await addTenant(request);
      if (!ok) throw new Error("Failed to add tenant");
      showSuccess("Tenant added successfully");
      await loadTenants();
      return;
    }

    if (!selectedTenant) throw new Error("No tenant selected");

    const ok = await updateTenant(selectedTenant.id, request);
    if (!ok) throw new Error("Failed to update tenant");
    showSuccess("Tenant updated successfully");
    await loadTenants();
  };

  const filteredTenants = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return tenants;
    return tenants.filter((t) => t.name.toLowerCase().includes(query));
  }, [tenants, searchQuery]);

  const columns: ColumnDefinition<TenantDto>[] = useMemo(
    () => [
      {
        id: "name",
        label: "Tenant Name",
        format: (tenant) => tenant.name,
      },
      {
        id: "status",
        label: "Status",
        format: (tenant) => {
          const label = toUiStatusLabel(tenant.status);
          const isActive = label === "Active";

          return (
            <Chip
              label={label}
              size="small"
              className={isActive ? "status-chip-enabled" : "status-chip-disabled"}
            />
          );
        },
      },
    ],
    []
  );

  const requestDelete = (tenant: TenantDto) => {
    setTenantPendingDelete(tenant);
    setDeleteDialogOpen(true);
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setDeleteDialogOpen(false);
    setTenantPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!tenantPendingDelete) return;

    try {
      setIsDeleting(true);
      const ok = await deleteTenant(tenantPendingDelete.id);
      if (!ok) {
        showError("Failed to delete tenant");
        return;
      }

      showSuccess("Tenant deleted successfully");
      await loadTenants();

      setDeleteDialogOpen(false);
      setTenantPendingDelete(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to delete tenant";
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ color: "hsl(19 101% 51% / 1)" }}>
          Tenants
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => void openAdd()}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
          }}
        >
          Add Tenant
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: "hsl(19 101% 51% / 1)" }} />
        </Box>
      )}

      {error && !loading && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#f44336" },
          }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box>
          <SearchBar
            placeholder="Search tenants by name..."
            value={searchQuery}
            onChange={setSearchQuery}
          />

          <GenericTable
            columns={columns}
            data={filteredTenants}
            getRowKey={(tenant) => tenant.id}
            loading={false}
            emptyMessage={
              searchQuery
                ? "No tenants found matching your search"
                : "No tenants available"
            }
            actionsColumn={{
              label: "Actions",
              align: "right",
              render: (tenant) => (
                <Box className="user-table-actions">
                  <IconButton
                    aria-label="edit"
                    onClick={() => void openEdit(tenant.id)}
                    className="user-table-edit-button"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => requestDelete(tenant)}
                    className="user-table-delete-button"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ),
            }}
            showResultsCount={true}
          />
        </Box>
      )}

      <TenantUpsertDialog
        open={dialogOpen}
        mode={dialogMode}
        tenant={selectedTenant}
        availableFeatures={availableFeatures}
        initialFeatureKeys={selectedTenantFeatureKeys}
        featuresLoading={featuresLoading}
        tenantFeaturesLoading={tenantFeaturesLoading}
        onClose={closeDialog}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete tenant"
        message={
          tenantPendingDelete
            ? `Are you sure you want to delete ${tenantPendingDelete.name}? This action cannot be undone.`
            : "Are you sure you want to delete this tenant?"
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </Container>
  );
};

export default TenantManagement;